import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Package,
  Plus,
  MagnifyingGlass,
  Funnel,
  Warning,
  DotsThree,
  Eye,
  PencilSimple,
  Trash,
  ArrowRight,
  UploadSimple
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import ImportModal from '../../components/ImportModal';

const categories = ['Reagent', 'Enzyme', 'Buffer', 'Media', 'Antibiotic', 'Chemical', 'Consumable', 'Equipment', 'Other'];

const InventoryPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showAlerts, setShowAlerts] = useState(false);
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    loadInventory();
    loadAlerts();
  }, [categoryFilter]);

  const loadInventory = async () => {
    try {
      const params = {};
      if (categoryFilter) params.category = categoryFilter;
      const data = await api.getInventory(params);
      setItems(data);
    } catch (error) {
      console.error('Failed to load inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    try {
      const data = await api.getInventoryAlerts();
      setAlerts(data);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await api.deleteInventoryItem(id);
        setItems(items.filter(i => i.id !== id));
        toast.success('Item deleted');
      } catch (error) {
        toast.error('Failed to delete item');
      }
    }
  };

  const getStockStatus = (item) => {
    if (item.quantity <= 0) return { class: 'bg-red-100 text-red-400', label: 'Out of stock' };
    if (item.quantity <= item.minimum_quantity) return { class: 'bg-amber-100 text-amber-300', label: 'Low stock' };
    return { class: 'bg-green-100 text-green-400', label: 'In stock' };
  };

  const filteredItems = items.filter(item =>
    item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.catalog_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.supplier?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalAlerts = (alerts?.low_stock?.length || 0) + (alerts?.expiring_soon?.length || 0) + (alerts?.expired?.length || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="inventory-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">Inventory</h1>
          <p className="text-zinc-500 mt-1">Manage lab reagents and supplies</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="btn-secondary flex items-center gap-2" onClick={() => setShowImport(true)} data-testid="import-inventory-btn">
            <UploadSimple size={16} />
            Import
          </Button>
          <Link to="/inventory/new">
            <Button className="btn-primary flex items-center gap-2" data-testid="new-inventory-btn">
              <Plus size={16} />
              Add Item
            </Button>
          </Link>
        </div>
      </div>

      {/* Alerts Banner */}
      {totalAlerts > 0 && (
        <div className="bg-amber-900/20 border border-amber-800/40 rounded-sm p-4 mb-6 flex items-center gap-3" data-testid="inventory-alerts">
          <Warning size={20} className="text-amber-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">
              {totalAlerts} item{totalAlerts !== 1 ? 's' : ''} need attention
            </p>
            <p className="text-xs text-amber-400">
              {alerts?.low_stock?.length || 0} low stock, {alerts?.expiring_soon?.length || 0} expiring, {alerts?.expired?.length || 0} expired
            </p>
          </div>
          <Button variant="outline" size="sm" className="btn-secondary" onClick={() => setShowAlerts(!showAlerts)}>
            {showAlerts ? 'Hide' : 'View'} <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
      )}

      {/* Alert Details */}
      {showAlerts && alerts && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {alerts.low_stock?.length > 0 && (
            <div className="card-flat p-4">
              <h3 className="text-sm font-medium text-amber-300 mb-2">Low Stock ({alerts.low_stock.length})</h3>
              <div className="space-y-1">
                {alerts.low_stock.slice(0, 5).map(item => (
                  <Link key={item.id} to={`/inventory/${item.id}`} className="text-sm text-zinc-700 hover:text-indigo-300 block truncate">
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {alerts.expiring_soon?.length > 0 && (
            <div className="card-flat p-4">
              <h3 className="text-sm font-medium text-orange-700 mb-2">Expiring Soon ({alerts.expiring_soon.length})</h3>
              <div className="space-y-1">
                {alerts.expiring_soon.slice(0, 5).map(item => (
                  <Link key={item.id} to={`/inventory/${item.id}`} className="text-sm text-zinc-700 hover:text-indigo-300 block truncate">
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {alerts.expired?.length > 0 && (
            <div className="card-flat p-4">
              <h3 className="text-sm font-medium text-red-400 mb-2">Expired ({alerts.expired.length})</h3>
              <div className="space-y-1">
                {alerts.expired.slice(0, 5).map(item => (
                  <Link key={item.id} to={`/inventory/${item.id}`} className="text-sm text-zinc-700 hover:text-indigo-300 block truncate">
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="card-flat p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 input-field w-full"
              data-testid="search-inventory"
            />
          </div>
          <div className="flex items-center gap-2">
            <Funnel size={18} className="text-zinc-400" />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="input-field"
              data-testid="category-filter"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="card-flat overflow-hidden">
        {filteredItems.length > 0 ? (
          <table className="data-table" data-testid="inventory-table">
            <thead>
              <tr>
                <th className="table-header">Name</th>
                <th className="table-header">Category</th>
                <th className="table-header">Quantity</th>
                <th className="table-header">Status</th>
                <th className="table-header">Location</th>
                <th className="table-header">Expiration</th>
                <th className="table-header w-12"></th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const status = getStockStatus(item);
                return (
                  <tr key={item.id} className="table-row" data-testid={`inventory-row-${item.id}`}>
                    <td className="table-cell">
                      <Link to={`/inventory/${item.id}`} className="font-medium text-slate-100 hover:text-indigo-300">
                        {item.name}
                      </Link>
                      {item.catalog_number && (
                        <p className="text-xs text-zinc-500">{item.catalog_number}</p>
                      )}
                    </td>
                    <td className="table-cell">
                      <span className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-sm">
                        {item.category}
                      </span>
                    </td>
                    <td className="table-cell font-mono">
                      {item.quantity} {item.unit}
                    </td>
                    <td className="table-cell">
                      <span className={`text-xs px-2 py-0.5 rounded-sm ${status.class}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="table-cell text-zinc-500">
                      {item.location || '-'}
                    </td>
                    <td className="table-cell text-zinc-500">
                      {item.expiration_date ? new Date(item.expiration_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="table-cell">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 hover:bg-zinc-100 rounded-sm">
                            <DotsThree size={20} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/inventory/${item.id}`)}>
                            <Eye size={16} className="mr-2" /> View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/inventory/${item.id}/edit`)}>
                            <PencilSimple size={16} className="mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-red-400">
                            <Trash size={16} className="mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <Package size={48} className="text-zinc-300 mb-4" />
            <h3 className="heading-card text-slate-100 mb-2">No inventory items</h3>
            <p className="text-sm text-zinc-500 mb-4">Start tracking your lab supplies</p>
            <Link to="/inventory/new">
              <Button className="btn-primary">Add Item</Button>
            </Link>
          </div>
        )}
      </div>

      {showImport && (
        <ImportModal type="inventory" onClose={() => setShowImport(false)} onImportComplete={() => loadInventory()} />
      )}
    </div>
  );
};

export default InventoryPage;
